import React, { useState, useEffect, useCallback } from 'react';
import './AgendaTab.css';
import './WorkoutTab.css';

const HOUR_PX = 56;
const DIAS_A_FRENTE = 7; // hoje + próximos 7 dias

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const limparTitulo = (t) => (t || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
const toMin = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

// Evento "dia inteiro": aniversário, ou sem horário real (o Atalho manda
// 00:00 pra esses). Os demais vão pra linha do tempo.
const isDiaInteiro = (e) => {
  const t = (e.title || '').toLowerCase();
  return (
    t.includes('aniversário') || t.includes('birthday') ||
    e.all_day === 1 || !/^\d{1,2}:\d{2}$/.test(e.start_raw || '') || e.start_raw === '00:00'
  );
};

const isCancelado = (e) => (e.title || '').toLowerCase().includes('cancelado');

// Duração real quando o Atalho manda o fim (end_raw); sem fim, assume 1h.
// Eventos que se sobrepõem dividem a largura em colunas.
function layoutEventos(timed) {
  const evs = timed
    .map((e) => {
      const start = toMin(e.start_raw);
      const end = e.end_raw ? Math.max(toMin(e.end_raw), start + 15) : start + 60;
      return { e, start, end };
    })
    .sort((a, b) => a.start - b.start);
  const grupos = [];
  let cur = [];
  let curEnd = -1;
  evs.forEach((ev) => {
    if (cur.length && ev.start >= curEnd) { grupos.push(cur); cur = []; curEnd = -1; }
    cur.push(ev);
    curEnd = Math.max(curEnd, ev.end);
  });
  if (cur.length) grupos.push(cur);

  const out = [];
  grupos.forEach((g) => {
    const cols = [];
    g.forEach((ev) => {
      let c = cols.findIndex((fim) => fim <= ev.start);
      if (c === -1) { c = cols.length; cols.push(0); }
      cols[c] = ev.end;
      ev.col = c;
    });
    g.forEach((ev) => { ev.ncols = cols.length; out.push(ev); });
  });
  return out;
}

function AgendaTab({ svc }) {
  const hoje = toISO(new Date());
  const [events, setEvents] = useState([]);
  const [selecionado, setSelecionado] = useState(hoje);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agora, setAgora] = useState(new Date());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await svc.getCalendarEvents(DIAS_A_FRENTE);
      if (d.error) throw new Error(d.error);
      setEvents(d.events || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  // Atualiza a linha de "agora" a cada minuto.
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const dias = Array.from({ length: DIAS_A_FRENTE + 1 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });

  const doDia = events.filter((e) => e.event_date === selecionado);
  const diaInteiro = doDia.filter(isDiaInteiro);
  const timed = doDia.filter((e) => !isDiaInteiro(e));
  const posicionados = layoutEventos(timed);

  // Faixa de horas visível: 06h–20h, ampliada se algum evento sair disso.
  const hIni = Math.max(0, Math.min(6, Math.floor(Math.min(...posicionados.map((p) => p.start / 60), 6))));
  const hFim = Math.min(24, Math.max(20, Math.ceil(Math.max(...posicionados.map((p) => p.end / 60), 0)) + 1));
  const horas = Array.from({ length: hFim - hIni }, (_, i) => hIni + i);

  const dSel = new Date(`${selecionado}T12:00:00`);
  const semana = dSel.toLocaleDateString('pt-BR', { weekday: 'long' });
  const titulo = `${semana.charAt(0).toUpperCase() + semana.slice(1)} – ${dSel.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  const agoraMin = agora.getHours() * 60 + agora.getMinutes();
  const mostrarAgora = selecionado === hoje && agoraMin >= hIni * 60 && agoraMin <= hFim * 60;

  return (
    <div className="cal-view">
      {error && <p className="wk-msg wk-err">{error}</p>}

      <div className="cal-strip">
        {dias.map((d) => {
          const iso = toISO(d);
          const temEvento = events.some((e) => e.event_date === iso);
          return (
            <button
              key={iso}
              className={`cal-day ${iso === selecionado ? 'sel' : ''} ${iso === hoje ? 'today' : ''}`}
              onClick={() => setSelecionado(iso)}
            >
              <span className="cal-day-letter">{d.toLocaleDateString('pt-BR', { weekday: 'narrow' })}</span>
              <span className="cal-day-num">{d.getDate()}</span>
              <span className={`cal-day-dot ${temEvento ? 'on' : ''}`} />
            </button>
          );
        })}
      </div>

      <div className="cal-title">{titulo}</div>

      {loading && events.length === 0 && <p className="wk-msg">Carregando…</p>}

      {diaInteiro.length > 0 && (
        <div className="cal-allday">
          <span className="cal-allday-label">dia inteiro</span>
          <div className="cal-allday-chips">
            {diaInteiro.map((e, i) => (
              <span key={e.id ?? i} className="cal-chip">{limparTitulo(e.title)}</span>
            ))}
          </div>
        </div>
      )}

      <div className="cal-grid" style={{ height: horas.length * HOUR_PX }}>
        {horas.map((h) => (
          <div key={h} className="cal-hour" style={{ top: (h - hIni) * HOUR_PX }}>
            <span className="cal-hour-label">{pad(h)}:00</span>
            <span className="cal-hour-line" />
          </div>
        ))}

        <div className="cal-events">
          {posicionados.map((p, i) => (
            <div
              key={p.e.id ?? i}
              className={`cal-event ${isCancelado(p.e) ? 'cancelled' : ''}`}
              style={{
                top: (p.start / 60 - hIni) * HOUR_PX + 1,
                height: Math.max(22, ((p.end - p.start) / 60) * HOUR_PX - 3),
                left: `${(p.col / p.ncols) * 100}%`,
                width: `calc(${100 / p.ncols}% - 4px)`,
              }}
            >
              <span className="cal-event-title">{limparTitulo(p.e.title)}</span>
              <span className="cal-event-time">
                {p.e.start_raw}{p.e.end_raw ? ` – ${p.e.end_raw}` : ''}
              </span>
            </div>
          ))}

          {mostrarAgora && (
            <div className="cal-now" style={{ top: (agoraMin / 60 - hIni) * HOUR_PX }}>
              <span className="cal-now-dot" />
            </div>
          )}
        </div>
      </div>

      {!loading && doDia.length === 0 && <p className="wk-msg">Nenhum compromisso neste dia.</p>}
      <div className="wk-spacer" />
    </div>
  );
}

export default AgendaTab;
